import { useConfig, useValidation, type Rules } from '@seek/libs-shared';
import {
  Box,
  Column,
  Columns,
  Heading,
  IconThumb,
  Stack,
  Text,
} from 'braid-design-system';
import { useEffect } from 'react';
import { DEFAULT_VARIANT_SCHEMA_VERSION } from '../../../shared/constants/launchDarklyFeatureFlags';
import {
  CURRENT_PAGE_WORK_LIFE_BALANCE,
  trackContinuePressed,
  trackDisplayPage,
  trackSkipQuestionPressed,
} from '../../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayoutNoStepper } from '../../components/ReviewPageLayout/ReviewPageLayoutNoStepper';
import type { Rating } from '../../components/StarSelector/StarSelector';
import { WorkLifeBalanceRatingQuestion } from '../../questions/RatingQuesions/WorkLifeBalanceRatingQuestion';
import { WorkLifeBalanceDetailsQuestion } from '../../questions/SimpleQuestions/WorkLifeBalanceDetailsQuestion';
import {
  ExperimentTwoProgress,
  type PageProps,
  type ReviewPayload,
} from '../../type';
import { validationRules } from '../../validation/validationRules';

type Props = PageProps & {
  isSubmitting: boolean;
};

export const WorkLifeBalancePage = ({
  review,
  reviewFormContext,
  setPage,
  setter,
  pageNumber,
  isSubmitting,
  onSubmit,
}: Props) => {
  const config = useConfig();

  useEffect(() => {
    if (reviewFormContext?.variant) {
      trackDisplayPage({
        config,
        context: reviewFormContext,
        currentPage: CURRENT_PAGE_WORK_LIFE_BALANCE,
        variant: reviewFormContext.variant,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFormContext?.variant]);

  const fields = {
    workLifeBalanceDescription: review?.workLifeBalanceDescription,
  };

  const { workLifeBalanceDescription } = validationRules(config.language);

  const rules = {
    workLifeBalanceDescription,
  } as Rules<typeof fields>;

  const {
    getTone,
    getMessage,
    handleValueChangeWithValidation,
    validateWithErrors,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOnContinue = () => {
    const { isValid } = validateWithErrors();

    if (onSubmit) {
      onSubmit();
    }

    trackContinuePressed({
      config,
      context: reviewFormContext,
      validation: isValid ? 'success' : 'mandatory_questions_not_answered',
      currentPage: CURRENT_PAGE_WORK_LIFE_BALANCE,
      variant: reviewFormContext.variant ?? DEFAULT_VARIANT_SCHEMA_VERSION,
    });

    if (!isValid) {
      return;
    }
  };

  const handleOnSkip = () => {
    trackSkipQuestionPressed({
      config,
      context: reviewFormContext,
      currentPage: CURRENT_PAGE_WORK_LIFE_BALANCE,
      variant: reviewFormContext.variant ?? DEFAULT_VARIANT_SCHEMA_VERSION,
    });

    setter({
      ...review,
      workLifeBalanceRating: undefined,
      workLifeBalanceDescription: undefined,
    });

    setPage(ExperimentTwoProgress.PerksAndBenefits);
  };

  const handleOnChange =
    (updateProperty: keyof ReviewPayload) => (value: Rating) => {
      setter({
        ...review,
        [updateProperty]: value,
      });
    };

  return (
    <>
      <Box background="infoLight" borderRadius="large" padding="medium">
        <Columns space="small" alignY="top">
          <Column width="content">
            <IconThumb />
          </Column>
          <Column>
            <Box paddingY="xxsmall">
              <Stack space="medium">
                <Text weight="strong">
                  You’ve submitted your initial review
                </Text>

                <Text>Want to add more detail?</Text>
              </Stack>
            </Box>
          </Column>
        </Columns>
      </Box>
      <ReviewPageLayoutNoStepper
        progress={pageNumber}
        onContinue={handleOnContinue}
        onSkip={handleOnSkip}
        isContinueLoading={isSubmitting}
      >
        <Stack space="large">
          <Heading level="1">Work life balance</Heading>
          <Stack space="small">
            <WorkLifeBalanceRatingQuestion
              onChange={handleOnChange('workLifeBalanceRating')}
              value={review?.workLifeBalanceRating}
              title="Rate your work/life balance"
              isRequired={true}
            />
            <WorkLifeBalanceDetailsQuestion
              onChange={handleValueChangeWithValidation({
                setter,
                validatorProperty: 'workLifeBalanceDescription',
                previousValue: review,
              })}
              tone={getTone('workLifeBalanceDescription')}
              message={getMessage('workLifeBalanceDescription')}
              value={review?.workLifeBalanceDescription}
            />
          </Stack>
        </Stack>
      </ReviewPageLayoutNoStepper>
    </>
  );
};
